import { createEffect, createEvent, createStore } from "effector";
import { Activity, ActivityAvailability } from "../models";
import { ActivitiesEndpoint } from "../endpoints/activities";
import { confirmChangeActivities } from "./confirmChangeFns";
import { resetAllSelectedStores } from "./resetAllSelected";

const resetSelectedActivities = createEvent("reset selected activities");
export type SelectedActivity = {
  entity: Activity;
  slotDuration: number;
  personTypes: {
    id: number;
    name: string;
    quantity: number;
  }[];
  guestsQuantity: number;
  timeSlots: number; // in timeSlots (not minutes!)
  availabilitySlot: ActivityAvailability;
  availabilitySlots: ActivityAvailability[];
  instancesIds: string[];
};
export const $selectedActivities = createStore<SelectedActivity[]>([])
  .reset(resetSelectedActivities)
  .reset(resetAllSelectedStores);

// ASYNC EFFECTS
export const selectedActivitiesApi = {
  save: createEffect(
    async (
      value: Omit<SelectedActivity, "guestsQuantity">
    ): Promise<SelectedActivity> => {
      if (!(await confirmChangeActivities())) {
        throw new Error("User declined activity change.");
      }

      return {
        ...value,
        guestsQuantity: value.personTypes.reduce(
          (acc, { quantity }) => acc + quantity,
          0
        ),
      };
    }
  ),
  remove: createEffect(
    async (value: { id: number }): Promise<{ id: number }> => {
      if (!(await confirmChangeActivities())) {
        throw new Error("User declined activity change.");
      }

      const store = $selectedActivities.getState();
      const idx = store.findIndex(({ entity: { id } }) => id === value.id);

      if (~idx) {
        const { instancesIds } = store[idx];
        await ActivitiesEndpoint.cancelReservation.call(instancesIds);
      }

      return value;
    }
  ),
  cancelAllActivitiesReserve: createEffect(
    async (): Promise<boolean | Error> => {
      const packages = $selectedActivities.getState();

      const instancesIds = packages.reduce<string[]>(
        (acc, { instancesIds }) => [...acc, ...instancesIds],
        []
      );

      if (instancesIds.length === 0) return true;

      try {
        await ActivitiesEndpoint.cancelReservation.call(instancesIds);
        return true;
      } catch (e) {
        throw new Error(
          `Error while cancelling instances reserving: ${instancesIds}`
        );
      }
    }
  ),
  reset: async (): Promise<boolean> => {
    try {
      await selectedActivitiesApi.cancelAllActivitiesReserve();
      resetSelectedActivities();
      return true;
    } catch (e) {
      return false;
    }
  },
};

// STORES CHANGES
$selectedActivities.on(selectedActivitiesApi.save.doneData, (store, value) => {
  const storeCloned = [...store];

  const idx = storeCloned.findIndex(
    ({ entity: { id } }) => id === value.entity.id
  );
  if (~idx) {
    storeCloned[idx] = value;
  } else {
    storeCloned.push(value);
  }

  return storeCloned;
});

$selectedActivities.on(
  selectedActivitiesApi.remove.doneData,
  (store, value) => {
    const storeCloned = [...store];

    const idx = storeCloned.findIndex(({ entity: { id } }) => id === value.id);
    if (~idx) storeCloned.splice(idx, 1);

    return storeCloned;
  }
);

$selectedActivities.on(
  selectedActivitiesApi.cancelAllActivitiesReserve.doneData,
  (store) => {
    return [...store].map((item) => {
      return {
        ...item,
        instancesIds: [],
      };
    });
  }
);
